import { SecurityId } from "@fartherfinance/frontend/api/Types";

export type ExclusionsList = {
  [securityId: SecurityId]: boolean;
};

export type SecurityForExclusion = {
  securityId: SecurityId;
  isInPortfolio: boolean;
  isExcluded: boolean;
  isTLHSecurity: boolean;
  isSweepAccountSecurity: boolean;
};

const markAllSecuritiesHelper = (
  data: SecurityForExclusion[]
): ExclusionsList => {
  const allSelected = data.reduce<ExclusionsList>((acc, security) => {
    // disregard securities that are part of TLH
    if (security.isTLHSecurity === true) {
      return acc;
    }

    // disregard sweep account securities
    if (security.isSweepAccountSecurity === true) {
      return acc;
    }

    // if the security is already excluded, skip it
    if (security.isExcluded === true) {
      return acc;
    }

    acc[security.securityId] = true;
    return acc;
  }, {});

  return allSelected;
};

const unmarkAllSecuritiesHelper = (
  data: SecurityForExclusion[]
): ExclusionsList => {
  const unselectAll = data.reduce<ExclusionsList>((acc, security) => {
    // disregard securities that are part of TLH
    if (security.isTLHSecurity === true) {
      return acc;
    }

    // disregard sweep account securities
    if (security.isSweepAccountSecurity === true) {
      return acc;
    }

    // if the security is excluded, remove it
    if (security.isExcluded === true) {
      acc[security.securityId] = false;
    }

    return acc;
  }, {});

  return unselectAll;
};

/**
 * Creates a complete list of securities for the backend by combining the existing
 * list of excluded securities with the user's changes.
 *
 * @param data The full list of securities with their current exclusion status
 * @param clientChanges The client-side changes made by the user
 * @returns A complete list of security objects ready to be sent to the API
 */
const createCompleteExclusionsList = (
  data: SecurityForExclusion[],
  clientChanges: ExclusionsList
): { securityId: SecurityId }[] => {
  // Filter out securities that are part of Tax Loss Harvesting (TLH)
  // Then map to get the final list of excluded securities
  return data
    .filter((security) => !security.isTLHSecurity) // Filter out TLH securities
    .filter((security) => !security.isSweepAccountSecurity) // Filter out sweep account securities
    .filter((security) => {
      // Determine if this security should be excluded by checking:
      // 1. Client changes first (if present)
      // 2. Fall back to existing status if no client changes
      if (security.securityId in clientChanges) {
        return clientChanges[security.securityId];
      }
      return security.isExcluded;
    })
    .map((security) => ({
      securityId: security.securityId,
    }));
};

/**
 * Helper function to filter holdings uniformly across the application
 * This ensures consistent behavior in DoNotSellButtonV2 and usePortfolioHoldingsWithExcludedSecurities
 *
 * @param holdings Array of holdings to filter
 * @returns Filtered array of holdings
 */
export const filterHoldings = <
  T extends {
    asset_name?: string | null;
    farther_security_id?: string | null;
    ticker?: string | null;
    quantity?: number | null;
  }
>(
  holdings: T[]
): T[] => {
  // First filter out CASH holdings and securities with zero quantity
  const filteredHoldings = holdings.filter((holding) => {
    // Filter out by asset name "CASH"
    if (holding.ticker === "CASH") {
      return false;
    }

    // Filter out securities with zero quantity or nullish
    if (holding.quantity === 0 || Boolean(holding.quantity) === false) {
      return false;
    }

    return true;
  });

  // Then filter out duplicates by security ID
  const uniqueSecurityIds = new Set<string>();
  return filteredHoldings.filter((holding) => {
    // Skip if we've already seen this security ID and it exists
    if (
      holding.farther_security_id &&
      uniqueSecurityIds.has(holding.farther_security_id)
    ) {
      return false; // Skip this duplicate
    }

    // Add this securityId to the set of seen IDs if it exists
    if (holding.farther_security_id) {
      uniqueSecurityIds.add(holding.farther_security_id);
    }

    return true;
  });
};

export {
  markAllSecuritiesHelper,
  unmarkAllSecuritiesHelper,
  createCompleteExclusionsList,
};
